import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  ProductHierarchySliceState,
  UpdateProductLineInput,
  ProductLineGroup,
  ProductLineGroupInput,
  ProductLineDetail,
} from 'src/types/productLines/productHierarchy.types';
import { MandatoryAttribute } from 'src/services/api/productHierarchyService';
import { transformProductHierarchyData } from '../../components/manage-programs/utils/productHierarchyTransformUtils';
import { enrichProductHierarchyData } from '../../components/manage-programs/utils/productHierarchyUtils';
import { productHierarchyService } from '../../services/api/productHierarchyService';

// Cache timeout: 5 minutes in milliseconds
const CACHE_TIMEOUT_MS = 5 * 60 * 1000;

// Initial state for product hierarchy
const initialState: ProductHierarchySliceState = {
  productHierarchy: {
    data: [],
    isLoading: false,
    error: null,
    hasAttemptedFetch: false,
    hasSuccessfulFetch: false,
    lastFetchTimestamp: null,
  },
  productHierarchyMinimal: {
    data: [],
    isLoading: false,
    error: null,
    hasAttemptedFetch: false,
    hasSuccessfulFetch: false,
    lastFetchTimestamp: null,
  },
  mandatoryAttributes: {
    data: [],
    isLoading: false,
    error: null,
    hasAttemptedFetch: false,
    hasSuccessfulFetch: false,
    lastFetchTimestamp: null,
  },
  productLineGroups: {
    data: [],
    isLoading: false,
    error: null,
    hasAttemptedFetch: false,
    hasSuccessfulFetch: false,
    lastFetchTimestamp: null,
  },
  productLineDetails: {
    data: [],
    isLoading: false,
    error: null,
    hasAttemptedFetch: false,
    hasSuccessfulFetch: false,
    lastFetchTimestamp: null,
  },
};

// Async thunk action for updating a product line
export const updateProductLine = createAsyncThunk<void, UpdateProductLineInput, { rejectValue: string }>(
  'productHierarchy/updateProductLine',
  async (input: UpdateProductLineInput, { rejectWithValue }) => {
    try {
      await productHierarchyService.updateProductLineAPI(input);
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to update product line');
    }
  }
);

// Async thunk action for fetching product hierarchy (Product Lines with nested RPNs and FPNs)
export const fetchProductHierarchy = createAsyncThunk(
  'productHierarchy/fetchProductHierarchy',
  async (forceRefresh: boolean = false, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { productHierarchy: ProductHierarchySliceState };
      const { hasSuccessfulFetch, lastFetchTimestamp } = state.productHierarchy.productHierarchy;

      // Check if we should skip fetch (unless forced refresh)
      if (!forceRefresh && hasSuccessfulFetch && lastFetchTimestamp) {
        const cacheAge = Date.now() - lastFetchTimestamp;
        if (cacheAge < CACHE_TIMEOUT_MS) {
          // Cache is still valid, return existing data (already transformed)
          return state.productHierarchy.productHierarchy.data;
        }
      }

      // Fetch data from API
      const data = await productHierarchyService.fetchProductHierarchyAPI();

      // Step 1: Transform data - parse FPN countries and aggregate RPN FSD data
      const transformedData = transformProductHierarchyData(data);

      // Step 2: Enrich with metadata (id, hierarchy_level, hierarchy_name) for Ag-Grid
      return enrichProductHierarchyData(transformedData);
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to fetch product hierarchy');
    }
  }
);

// Async thunk action for fetching minimal product hierarchy (lightweight hierarchy for permissions)
export const fetchProductHierarchyMinimal = createAsyncThunk(
  'productHierarchy/fetchProductHierarchyMinimal',
  async (forceRefresh: boolean = false, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { productHierarchy: ProductHierarchySliceState };
      const { hasSuccessfulFetch, lastFetchTimestamp } = state.productHierarchy.productHierarchyMinimal;

      // Check if we should skip fetch (unless forced refresh)
      if (!forceRefresh && hasSuccessfulFetch && lastFetchTimestamp) {
        const cacheAge = Date.now() - lastFetchTimestamp;
        if (cacheAge < CACHE_TIMEOUT_MS) {
          // Cache is still valid, return existing data
          return state.productHierarchy.productHierarchyMinimal.data;
        }
      }

      // Fetch data from API
      const data = await productHierarchyService.fetchProductHierarchyMinimalAPI();
      return data;
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to fetch minimal product hierarchy');
    }
  }
);

// Async thunk action for fetching mandatory attributes (validation rules for attributes)
export const fetchMandatoryAttributes = createAsyncThunk(
  'productHierarchy/fetchMandatoryAttributes',
  async (forceRefresh: boolean = false, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { productHierarchy: ProductHierarchySliceState };
      const { hasSuccessfulFetch, lastFetchTimestamp } = state.productHierarchy.mandatoryAttributes;

      // Check if we should skip fetch (unless forced refresh)
      if (!forceRefresh && hasSuccessfulFetch && lastFetchTimestamp) {
        const cacheAge = Date.now() - lastFetchTimestamp;
        if (cacheAge < CACHE_TIMEOUT_MS) {
          // Cache is still valid, return existing data
          return state.productHierarchy.mandatoryAttributes.data;
        }
      }

      // Fetch data from API
      const data = await productHierarchyService.fetchMandatoryAttributesAPI();
      return data;
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to fetch mandatory attributes');
    }
  }
);

// Async thunk action for fetching product line groups (grouping configuration for product lines)
export const fetchProductLineGroups = createAsyncThunk(
  'productHierarchy/fetchProductLineGroups',
  async (forceRefresh: boolean = false, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { productHierarchy: ProductHierarchySliceState };
      const { hasSuccessfulFetch, lastFetchTimestamp } = state.productHierarchy.productLineGroups;

      // Check if we should skip fetch (unless forced refresh)
      if (!forceRefresh && hasSuccessfulFetch && lastFetchTimestamp) {
        const cacheAge = Date.now() - lastFetchTimestamp;
        if (cacheAge < CACHE_TIMEOUT_MS) {
          // Cache is still valid, return existing data
          return state.productHierarchy.productLineGroups.data;
        }
      }

      // Fetch data from API
      const data = await productHierarchyService.fetchProductLineGroupsAPI();
      return data;
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to fetch product line groups');
    }
  }
);

// Async thunk action for creating or updating product line groups
export const createOrUpdateProductLineGroups = createAsyncThunk<void, ProductLineGroupInput[], { rejectValue: string }>(
  'productHierarchy/createOrUpdateProductLineGroups',
  async (input: ProductLineGroupInput[], { rejectWithValue }) => {
    try {
      await productHierarchyService.createOrUpdateProductLineGroupsAPI(input);
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to create or update product line groups');
    }
  }
);

// Async thunk action for fetching product line details (flat list without hierarchy)
export const fetchProductLineDetails = createAsyncThunk(
  'productHierarchy/fetchProductLineDetails',
  async (forceRefresh: boolean = false, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { productHierarchy: ProductHierarchySliceState };
      const { hasSuccessfulFetch, lastFetchTimestamp } = state.productHierarchy.productLineDetails;

      // Check if we should skip fetch (unless forced refresh)
      if (!forceRefresh && hasSuccessfulFetch && lastFetchTimestamp) {
        const cacheAge = Date.now() - lastFetchTimestamp;
        if (cacheAge < CACHE_TIMEOUT_MS) {
          // Cache is still valid, return existing data
          return state.productHierarchy.productLineDetails.data;
        }
      }

      // Fetch data from API
      const data = await productHierarchyService.fetchProductLineDetailsAPI();
      return data;
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to fetch product line details');
    }
  }
);

// Create the slice
const productHierarchySlice = createSlice({
  name: 'productHierarchy',
  initialState,
  reducers: {
    clearError: (state) => {
      state.productHierarchy.error = null;
    },
    resetFetchStatus: (state) => {
      state.productHierarchy.hasAttemptedFetch = false;
      state.productHierarchy.hasSuccessfulFetch = false;
      state.productHierarchy.lastFetchTimestamp = null;
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // fetchProductHierarchy
      .addCase(fetchProductHierarchy.pending, (state) => {
        state.productHierarchy.isLoading = true;
        state.productHierarchy.error = null;
      })
      .addCase(fetchProductHierarchy.fulfilled, (state, action) => {
        state.productHierarchy.isLoading = false;
        state.productHierarchy.data = action.payload;
        state.productHierarchy.hasAttemptedFetch = true;
        state.productHierarchy.hasSuccessfulFetch = true;
        state.productHierarchy.lastFetchTimestamp = Date.now();
        state.productHierarchy.error = null;
      })
      .addCase(fetchProductHierarchy.rejected, (state, action) => {
        state.productHierarchy.isLoading = false;
        state.productHierarchy.error = action.payload as string;
        state.productHierarchy.hasAttemptedFetch = true;
      })
      // fetchProductHierarchyMinimal
      .addCase(fetchProductHierarchyMinimal.pending, (state) => {
        state.productHierarchyMinimal.isLoading = true;
        state.productHierarchyMinimal.error = null;
      })
      .addCase(fetchProductHierarchyMinimal.fulfilled, (state, action) => {
        state.productHierarchyMinimal.isLoading = false;
        state.productHierarchyMinimal.data = action.payload;
        state.productHierarchyMinimal.hasAttemptedFetch = true;
        state.productHierarchyMinimal.hasSuccessfulFetch = true;
        state.productHierarchyMinimal.lastFetchTimestamp = Date.now();
        state.productHierarchyMinimal.error = null;
      })
      .addCase(fetchProductHierarchyMinimal.rejected, (state, action) => {
        state.productHierarchyMinimal.isLoading = false;
        state.productHierarchyMinimal.error = action.payload as string;
        state.productHierarchyMinimal.hasAttemptedFetch = true;
      })
      // fetchMandatoryAttributes
      .addCase(fetchMandatoryAttributes.pending, (state) => {
        state.mandatoryAttributes.isLoading = true;
        state.mandatoryAttributes.error = null;
      })
      .addCase(fetchMandatoryAttributes.fulfilled, (state, action) => {
        state.mandatoryAttributes.isLoading = false;
        state.mandatoryAttributes.data = action.payload as MandatoryAttribute[];
        state.mandatoryAttributes.hasAttemptedFetch = true;
        state.mandatoryAttributes.hasSuccessfulFetch = true;
        state.mandatoryAttributes.lastFetchTimestamp = Date.now();
        state.mandatoryAttributes.error = null;
      })
      .addCase(fetchMandatoryAttributes.rejected, (state, action) => {
        state.mandatoryAttributes.isLoading = false;
        state.mandatoryAttributes.error = action.payload as string;
        state.mandatoryAttributes.hasAttemptedFetch = true;
      })
      // fetchProductLineGroups
      .addCase(fetchProductLineGroups.pending, (state) => {
        state.productLineGroups.isLoading = true;
        state.productLineGroups.error = null;
      })
      .addCase(fetchProductLineGroups.fulfilled, (state, action) => {
        state.productLineGroups.isLoading = false;
        state.productLineGroups.data = action.payload as ProductLineGroup[];
        state.productLineGroups.hasAttemptedFetch = true;
        state.productLineGroups.hasSuccessfulFetch = true;
        state.productLineGroups.lastFetchTimestamp = Date.now();
        state.productLineGroups.error = null;
      })
      .addCase(fetchProductLineGroups.rejected, (state, action) => {
        state.productLineGroups.isLoading = false;
        state.productLineGroups.error = action.payload as string;
        state.productLineGroups.hasAttemptedFetch = true;
      })
      // fetchProductLineDetails
      .addCase(fetchProductLineDetails.pending, (state) => {
        state.productLineDetails.isLoading = true;
        state.productLineDetails.error = null;
      })
      .addCase(fetchProductLineDetails.fulfilled, (state, action) => {
        state.productLineDetails.isLoading = false;
        state.productLineDetails.data = action.payload as ProductLineDetail[];
        state.productLineDetails.hasAttemptedFetch = true;
        state.productLineDetails.hasSuccessfulFetch = true;
        state.productLineDetails.lastFetchTimestamp = Date.now();
        state.productLineDetails.error = null;
      })
      .addCase(fetchProductLineDetails.rejected, (state, action) => {
        state.productLineDetails.isLoading = false;
        state.productLineDetails.error = action.payload as string;
        state.productLineDetails.hasAttemptedFetch = true;
      });
  },
});

// Export actions
export const { clearError, resetFetchStatus, reset } = productHierarchySlice.actions;

// Export reducer
export default productHierarchySlice.reducer;
